//@ts-nocheck
import React, { useState, useRef } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import dataProvider from '../../providers/dataProvider';
import { PublicSettings } from './GeneralSettings';

interface GeneralSettingsFormProps {
  initialSettings: PublicSettings | null;
  onSave: (settings: PublicSettings) => Promise<void>;
}

export const GeneralSettingsForm = ({ initialSettings, onSave }: GeneralSettingsFormProps) => {
  const [quoteTextDe, setQuoteTextDe] = useState(initialSettings?.home?.quote?.text?.de || '');
  const [quoteTextEn, setQuoteTextEn] = useState(initialSettings?.home?.quote?.text?.en || '');
  const [author, setAuthor] = useState(initialSettings?.home?.quote?.author || '');
  const [imageUrl, setImageUrl] = useState(initialSettings?.home?.quote?.image || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, GIF, WebP, or SVG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Get presigned URL
      const uploadData = await dataProvider.getSettingsUploadUrl(file.name, file.type);

      // Upload file to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      // Set the public URL
      setImageUrl(uploadData.publicUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const settings: PublicSettings = {
        home: {
          quote: {
            text: {
              de: quoteTextDe,
              en: quoteTextEn,
            },
            author: author,
            image: imageUrl,
          },
        },
      };

      await onSave(settings);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ marginBottom: '20px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" sx={{ marginBottom: '20px', fontWeight: 'bold' }}>
        Home Quote Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Quote Text - German */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: '500' }}>
            Quote Text (German)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={quoteTextDe}
            onChange={(e) => setQuoteTextDe(e.target.value)}
            placeholder="Enter the quote text in German..."
            variant="outlined"
          />
        </Grid>

        {/* Quote Text - English */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: '500' }}>
            Quote Text (English)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={quoteTextEn}
            onChange={(e) => setQuoteTextEn(e.target.value)}
            placeholder="Enter the quote text in English..."
            variant="outlined"
          />
        </Grid>

        {/* Author */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: '500' }}>
            Author
          </Typography>
          <TextField
            fullWidth
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Enter the author name..."
            variant="outlined"
          />
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: '500' }}>
            Quote Image
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              onClick={handleChooseFile}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            {imageUrl && (
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                Image uploaded
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Image Preview */}
        {imageUrl && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ marginBottom: '8px', fontWeight: '500' }}>
              Image Preview
            </Typography>
            <Box
              sx={{
                maxWidth: '300px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
              }}
            >
              <img
                src={imageUrl}
                alt="Quote preview"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Save Button */}
      <Box sx={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};
